<template>
  <div class="advertiser-list-table">
    <div v-if="loader && localAdvertisers.length === 0" class="text-center">
      <Loader
        :size="'big'"
      />
    </div>
    <template v-else-if="user">
      <!-- FILTERS -->
      <div class="d-flex align-items-end justify-content-end mb-10">
        <div class="me-3">
          <form class="default-form" @submit.prevent="reset">
            <span class="fw-bold form-label me-3">{{ $t('name') }}:</span>
            <input
              type="text"
              class="form-control form-control-sm"
              placeholder=""
              v-model="filters.name"
            />
          </form>
        </div>
        <div class="me-3 default-form">
          <span class="fw-bold form-label me-3">{{ $t('category') }}:</span><br/>
          <template v-if="categoriesLoader">
            <Loader
              :size="'small'"
              :align="'start'"
            />
          </template>
          <div class="custom-select custom-select-sm" v-else>
            <select
              v-model="filters.category_id"
              class="form-control form-control-sm"
            >
              <option value="">{{ $t('all2') }}</option>
              <option :value="category.id" v-for="(category, index) in categories" :key="index">
                {{ $t('categories.' + category.slug) }}
              </option>
            </select>
          </div>
        </div>
        <div class="me-3">
          <span class="fw-bold form-label me-3">{{ $t('status') }}:</span><br/>
          <div class="default-form">
            <div class="custom-select custom-select-sm">
              <select
                v-model="filters.status"
                class="form-control form-control-sm"
              >
                <option value="">{{ $t('all') }}</option>
                <option value="ACTIVE">{{ $t('status_type.active') }}</option>
                <option value="INACTIVE">{{ $t('status_type.inactive') }}</option>
              </select>
            </div>
          </div>
        </div>
        <div>
          <Button
            type="button"
            class="btn-color10 btn-xs mt-3"
            @click="reset"
          >
            {{ $t('search') }}
          </Button>
        </div>
      </div> <!-- END FILTERS -->
      <div v-if="localAdvertisers.length === 0 && !loader" class="text-center">
        <router-link :to="{ name: 'advertisers.create' }" class="btn btn-color10 btn-lg mt-3">{{ $t('create_advertiser') }}</router-link>
      </div>
      <div class="d-flex flex-wrap justify-content-center gap-4">
        <div class="" v-for="(advertiser, index) in localAdvertisers" :key="index">
          <div class="card card-advertiser card-xl-stretch mb-xl-8 col-lg-4">
            <!--begin::Body-->
            <div class="card-body p-0">
              <!--begin::Header-->
              <div class="px-7 pt-5 h-275px w-100 bg-color13">
                <!--begin::Heading-->
                <div class="d-flex flex-stack justify-content-between">
                  <div class="d-flex align-items-center bg-light me-5 p-3 h-50px w-50px position-relative">
                    <img v-if="advertiser.logo" :src="advertiser.logo" alt="" />
                    <img v-else src="/images/v2/layout/blank.svg" alt="" />
                  </div>
                  <div class="d-flex gap-5 align-items-center">
                    <template v-if="advertiser.status === 'ERROR'">
                      <RetryAdvertiserCreationOnPlatformButton
                        :advertiserId="advertiser.id"
                        @retry-successful="reset"
                        @retry-error="handleRetryError"
                      />
                    </template>
                    <div class="dropdown">
                      <!--begin::Menu-->
                      <button type="button" class="btn btn-sm btn-icon btn-active-white btn-active-color-primary border-0 dropdown-toggle no-arrow" data-bs-toggle="dropdown">
                        <!--begin::Svg Icon | path: icons/duotune/general/gen024.svg-->
                        <span class="svg-icon svg-icon-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 0 24 24">
                            <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                              <circle cx="12" cy="5" r="2" fill="#1c2833"/>
                              <circle cx="12" cy="12" r="2" fill="#1c2833"/>
                              <circle cx="12" cy="19" r="2" fill="#1c2833"/>
                            </g>
                          </svg>
                        </span>
                        <!--end::Svg Icon-->
                      </button>
                      <!--begin::Menu 3-->
                      <div class="menu menu-sub menu-sub-dropdown dropdown-menu menu-column menu-gray-800 menu-state-bg-light-primary fw-semibold w-275px py-3 " data-kt-menu="true">
                        <!--begin::Menu item-->
                        <div class="menu-item px-3">
                          <router-link :to="{ name: 'advertisers.show', params: { id: advertiser.id } }" class="menu-link px-3" >{{ $t('details') }}</router-link>
                        </div>
                        <!--end::Menu item-->
                        <!--begin::Menu item-->
                        <div class="menu-item px-3">
                          <router-link :to="{ name: 'advertisers.edit', params: { id: advertiser.id } }" class="menu-link px-3">{{ $t('edit_advertiser') }}</router-link>
                        </div>
                        <!--end::Menu item-->
                        <!--begin::Menu item-->
                        <div
                            class="menu-item px-3"
                            v-if="advertiser.status === 'ACTIVE'"
                        >
                          <a href="#" @click.prevent="openDestroyAdvertiserModal(advertiser)" class="menu-link px-3">{{ $t('disable_advertiser') }}</a>
                        </div>
                        <!--end::Menu item-->
                        <!--begin::Menu item-->
                        <div
                          class="menu-item px-3 my-1"
                          v-else-if="advertiser.status === 'INACTIVE'"
                        >
                          <a href="#" @click.prevent="openActivateAdvertiserModal(advertiser)" class="menu-link px-3" >{{ $t('activate_advertiser') }}</a>
                        </div>
                        <!--end::Menu item-->
                        <div class="separator separator-dashed mx-5 my-5"></div>
                        <!--begin::Heading-->
                        <div class="menu-item px-3">
                          <div class="menu-content text-muted pb-2 px-3 fs-7 text-uppercase">Cupom</div>
                        </div>
                        <!--end::Heading-->
                        <!-- COUPON -->
                        <div class="menu-item px-3 my-1">
                          <template v-if="!advertiser.coupon">
                            <AddCoupon
                              :advertiser="advertiser"
                              @coupon-added="getAdvertisers"
                            />
                          </template>
                          <template v-else>
                            <div class="d-flex align-items-center">
                              <span class="fw-bold me-2">{{ $t('active_coupon') }}:</span>
                              <span class="bg-color2 text-white px-3 py-2 font-size-xSmall">{{ advertiser.coupon.code }}</span>
                              <Button
                                class="btn-xs btn-circle btn-danger font-size-small p-0 ms-2"
                                v-b-modal.destroy-coupon-modal
                                v-b-tooltip.hover :title="$t('logout')"
                              >
                                <i class="fa fa-trash"></i>
                              </Button>
                            </div>
                            <RemoveCouponModal
                              :advertiser="advertiser"
                              :coupon="advertiser.coupon"
                              @coupon-destroyed="getAdvertisers"
                            />
                          </template>
                        </div> <!-- END COUPON -->
                      </div>
                      <!--end::Menu 3-->
                      <!--end::Menu-->
                    </div>
                  </div>
                </div>
                <!--end::Heading-->
                <!--begin::Balance-->
                <div class="d-flex text-center flex-column py-5">
                  <router-link :to="{ name: 'advertisers.show', params: { id: advertiser.id } }" class="mb-2">
                    <span class="fw-bold fs-4">{{ advertiser.name }}</span>
                  </router-link>
                  <p class="fs-7 mb-2">{{ advertiser.website }}</p>
                  <p class="fs-7 mb-0">{{ advertiser.status | formatStatus }}</p>
                </div>
                <!--end::Balance-->
              </div>
              <!--end::Header-->
              <!--begin::Items-->
              <div class="bg-body shadow-sm mx-7 mb-7 p-5 position-relative z-index-1" style="margin-top: -80px">
                <!--begin::Item-->
                <div class="d-flex align-items-center mb-2">
                  <!--begin::Symbol-->
                  <div class="symbol symbol-50px me-3">
                    <div class="symbol-label bg-light-warning">
                      <span class="svg-icon svg-icon-1 svg-icon-primary">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M3.20001 5.91897L16.9 3.01895C17.4 2.91895 18 3.219 18.1 3.819L19.2 9.01895L3.20001 5.91897Z" fill="currentColor"/>
                          <path opacity="0.3" d="M13 13.9189C13 12.2189 14.3 10.9189 16 10.9189H21C21.6 10.9189 22 11.3189 22 11.9189V15.9189C22 16.5189 21.6 16.9189 21 16.9189H16C14.3 16.9189 13 15.6189 13 13.9189ZM16 12.4189C15.2 12.4189 14.5 13.1189 14.5 13.9189C14.5 14.7189 15.2 15.4189 16 15.4189C16.8 15.4189 17.5 14.7189 17.5 13.9189C17.5 13.1189 16.8 12.4189 16 12.4189Z" fill="currentColor"/>
                          <path d="M13 13.9189C13 12.2189 14.3 10.9189 16 10.9189H21V7.91895C21 6.81895 20.1 5.91895 19 5.91895H3C2.4 5.91895 2 6.31895 2 6.91895V20.9189C2 21.5189 2.4 21.9189 3 21.9189H19C20.1 21.9189 21 21.0189 21 19.9189V16.9189H16C14.3 16.9189 13 15.6189 13 13.9189Z" fill="currentColor"/>
                        </svg>
                      </span>
                    </div>
                  </div>
                  <!--end::Symbol-->
                  <!--begin::Title-->
                  <div>
                    <div class="fs-4 text-dark fw-bold">{{ advertiser.available_amount / 100 | formatMoney(advertiser.currency) }}</div>
                    <div class="fs-7 text-muted fw-bold">{{ $t('available') }}</div>
                  </div>
                  <!--end::Title-->
                </div>
                <!--end::Item-->
                <!--begin::Item-->
                <div class="d-flex align-items-center mb-2">
                  <!--begin::Symbol-->
                  <div class="symbol symbol-50px me-3">
                    <div class="symbol-label bg-light-warning">
                      <span class="svg-icon svg-icon-1 svg-icon-primary">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M12.5 22C11.9 22 11.5 21.6 11.5 21V3C11.5 2.4 11.9 2 12.5 2C13.1 2 13.5 2.4 13.5 3V21C13.5 21.6 13.1 22 12.5 22Z" fill="currentColor"/>
                          <path d="M17.8 14.7C17.8 15.5 17.6 16.3 17.2 16.9C16.8 17.6 16.2 18.1 15.3 18.4C14.5 18.8 13.5 19 12.4 19C11.1 19 10 18.7 9.10001 18.2C8.50001 17.8 8.00001 17.4 7.60001 16.7C7.20001 16.1 7 15.5 7 14.9C7 14.6 7.09999 14.3 7.29999 14C7.49999 13.8 7.80001 13.6 8.20001 13.6C8.50001 13.6 8.69999 13.7 8.89999 13.9C9.09999 14.1 9.29999 14.4 9.39999 14.7C9.59999 15.1 9.8 15.5 10 15.8C10.2 16.1 10.5 16.3 10.8 16.5C11.2 16.7 11.6 16.8 12.2 16.8C13 16.8 13.7 16.6 14.2 16.2C14.7 15.8 15 15.3 15 14.8C15 14.4 14.9 14 14.6 13.7C14.3 13.4 14 13.2 13.5 13.1C13.1 13 12.5 12.8 11.8 12.6C10.8 12.4 9.99999 12.1 9.39999 11.8C8.69999 11.5 8.19999 11.1 7.79999 10.6C7.39999 10.1 7.20001 9.39998 7.20001 8.59998C7.20001 7.89998 7.39999 7.19998 7.79999 6.59998C8.19999 5.99998 8.80001 5.60005 9.60001 5.30005C10.4 5.00005 11.3 4.80005 12.3 4.80005C13.1 4.80005 13.8 4.89998 14.5 5.09998C15.1 5.29998 15.6 5.60002 16 5.90002C16.4 6.20002 16.7 6.6 16.9 7C17.1 7.4 17.2 7.69998 17.2 8.09998C17.2 8.39998 17.1 8.7 16.9 9C16.7 9.3 16.4 9.40002 16 9.40002C15.7 9.40002 15.4 9.29995 15.3 9.19995C15.2 9.09995 15 8.80002 14.8 8.40002C14.6 7.90002 14.3 7.49995 13.9 7.19995C13.5 6.89995 13 6.80005 12.2 6.80005C11.5 6.80005 10.9 7.00005 10.5 7.30005C10.1 7.60005 9.79999 8.00002 9.79999 8.40002C9.79999 8.70002 9.9 8.89998 10 9.09998C10.1 9.29998 10.4 9.49998 10.6 9.59998C10.8 9.69998 11.1 9.90002 11.4 9.90002C11.7 10 12.1 10.1 12.7 10.3C13.5 10.5 14.2 10.7 14.8 10.9C15.4 11.1 15.9 11.4 16.4 11.7C16.8 12 17.2 12.4 17.4 12.9C17.6 13.4 17.8 14 17.8 14.7Z" fill="currentColor"/>
                        </svg>
                      </span>
                    </div>
                  </div>
                  <!--end::Symbol-->
                  <!--begin::Title-->
                  <div>
                    <div class="fs-4 text-dark fw-bold">{{ advertiser.allocated_amount / 100 | formatMoney(advertiser.currency) }}</div>
                    <div class="fs-7 text-muted fw-bold">{{ $t('allocated') }}</div>
                  </div>
                  <!--end::Title-->
                </div>
                <!--end::Item-->
                <!--begin::Item-->
                <div class="d-flex align-items-center me-2">
                  <!--begin::Symbol-->
                  <div class="symbol symbol-50px me-3">
                    <div class="symbol-label bg-light-warning">
                      <span class="svg-icon svg-icon-1 svg-icon-primary">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M20.9 12.9C20.3 12.9 19.9 12.5 19.9 11.9C19.9 11.3 20.3 10.9 20.9 10.9H21.8C21.3 6.2 17.6 2.4 12.9 2V2.9C12.9 3.5 12.5 3.9 11.9 3.9C11.3 3.9 10.9 3.5 10.9 2.9V2C6.19999 2.5 2.4 6.2 2 10.9H2.89999C3.49999 10.9 3.89999 11.3 3.89999 11.9C3.89999 12.5 3.49999 12.9 2.89999 12.9H2C2.5 17.6 6.19999 21.4 10.9 21.8V20.9C10.9 20.3 11.3 19.9 11.9 19.9C12.5 19.9 12.9 20.3 12.9 20.9V21.8C17.6 21.3 21.4 17.6 21.8 12.9H20.9Z" fill="currentColor"/>
                          <path d="M16.9 10.9H13.6C13.4 10.6 13.2 10.4 12.9 10.2V5.90002C12.9 5.30002 12.5 4.90002 11.9 4.90002C11.3 4.90002 10.9 5.30002 10.9 5.90002V10.2C10.6 10.4 10.4 10.6 10.2 10.9H9.89999C9.29999 10.9 8.89999 11.3 8.89999 11.9C8.89999 12.5 9.29999 12.9 9.89999 12.9H10.2C10.4 13.2 10.6 13.4 10.9 13.6V13.9C10.9 14.5 11.3 14.9 11.9 14.9C12.5 14.9 12.9 14.5 12.9 13.9V13.6C13.2 13.4 13.4 13.2 13.6 12.9H16.9C17.5 12.9 17.9 12.5 17.9 11.9C17.9 11.3 17.5 10.9 16.9 10.9Z" fill="currentColor"/>
                        </svg>
                      </span>
                    </div>
                  </div>
                  <!--end::Symbol-->
                  <!--begin::Title-->
                  <div>
                    <div class="fs-4 text-dark fw-bold">{{ advertiser.pending_amount / 100 | formatMoney(advertiser.currency) }}</div>
                    <div class="fs-7 text-muted fw-bold">{{ $t('pending') }}</div>
                  </div>
                  <!--end::Title-->
                </div>
                <!--end::Item-->
              </div>
              <!--end::Items-->
            </div>
            <!--end::Body-->
          </div>
        </div>
      </div>
      <!--begin::Pagination-->
      <div class="d-flex flex-stack flex-wrap pt-10" v-if="localAdvertisers.length > 0">
        <div class="fs-6 fw-semibold text-gray-700">
          {{ $t('pagination', { from: from, to: to, totalItems: totalItems }) }}
        </div>
        <!--begin::Pages-->
        <b-pagination
          class="custom-pagination"
          v-if="totalPages > 1"
          v-model="page"
          :total-rows="totalItems"
          :per-page="itemsPerPage"
          :loading="loader"
          @input="getAdvertisers"
          hide-goto-end-buttons
        />
        <!--end::Pages-->
      </div>
      <!--end::Pagination-->
      <ActivateAdvertiserModal
        :advertiser="selectedAdvertiser"
        @advertiser-activated="getAdvertisers"
      />
      <DestroyAdvertiserModal
        :advertiser="selectedAdvertiser"
        @advertiser-destroyed="getAdvertisers"
      />
    </template>
  </div>
</template>

<script>

import AdvertisersService from '@/modules/advertisers/services/advertisers-service'
import CategoriesService from '@/modules/categories/services/categories-service'
import { mapState } from 'vuex'

import ActivateAdvertiserModal from '@/components/advertisers/ActivateAdvertiserModal'
import AddCoupon from '@/components/coupons/AddCoupon'
import Button from '@/components/common/Button/Button'
import DestroyAdvertiserModal from '@/components/advertisers/DestroyAdvertiserModal'
import Loader from '@/components/common/Loader/Loader'
import RemoveCouponModal from '@/components/coupons/RemoveCouponModal'
import RetryAdvertiserCreationOnPlatformButton from '@/components/advertisers/RetryAdvertiserCreationOnPlatformButton'

export default {
  name: 'AdvertiserListTable',
  props: ['advertisers', 'reload'],
  components: {
    ActivateAdvertiserModal,
    AddCoupon,
    Button,
    DestroyAdvertiserModal,
    Loader,
    RemoveCouponModal,
    RetryAdvertiserCreationOnPlatformButton
  },
  data () {
    return {
      from: 1,
      itemsPerPage: 0,
      categories: [],
      categoriesLoader: false,
      filters: {
        category_id: '',
        name: '',
        status: ''
      },
      localAdvertisers: [],
      loader: false,
      page: 1,
      selectedAdvertiser: null,
      to: 9,
      totalItems: 0,
      totalPages: 0
    }
  },
  created () {
    if (this.advertisers) {
      this.localAdvertisers = this.advertisers
    } else {
      if (this.user) {
        this.reset()
        this.getCategories()
      }
    }
  },
  watch: {
    reload (newValue) {
      if (newValue) this.reset()
    },
    user (newUser) {
      this.reset()
      this.getCategories()
    }
  },
  computed: {
    ...mapState('users', {
      user: state => state.user
    })
  },
  methods: {
    /**
     * Get advertisers
     */
    async getAdvertisers () {
      if (!this.user) return false

      this.loader = true
      try {
        this.localAdvertisers = []
        const advertisers = await AdvertisersService.getAdvertisers({
          category_id: this.filters.category_id,
          companyId: this.user.company.id,
          name: this.filters.name,
          status: this.filters.status,
          page: this.page
        })
        this.localAdvertisers = advertisers.data
        this.totalItems = advertisers.meta.total
        this.totalPages = advertisers.meta.last_page
        this.itemsPerPage = advertisers.meta.per_page
        this.from = advertisers.meta.from
        this.to = advertisers.meta.to
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.loader = false
    },
    /**
     * Get categories
     */
    async getCategories () {
      this.categoriesLoader = true
      try {
        const categories = await CategoriesService.getCategories({
          company_id: this.user.company.id,
          type: 'ADVERTISER'
        })
        this.categories = categories.data
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.categoriesLoader = false
    },
    /**
     * Handle retry error
     */
    handleRetryError (error) {
      error.handleGlobally && error.handleGlobally()
    },
    /**
     * Open modal to activate advertiser
     */
    openActivateAdvertiserModal (advertiser) {
      this.selectedAdvertiser = advertiser
      this.$bvModal.show('activate-advertiser-modal')
    },
    /**
     * Open modal to destroy advertiser
     */
    openDestroyAdvertiserModal (advertiser) {
      this.selectedAdvertiser = advertiser
      this.$bvModal.show('destroy-advertiser-modal')
    },
    reset () {
      this.page = 1
      this.localAdvertisers = []
      this.getAdvertisers()
    }
  }
}
</script>

<style lang="scss" scoped>
.card-advertiser {
  width: 360px;
}
</style>
